import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../../Context/Context";
import RackFrontView from "./RackFrontView";
import useModalClose from "../../../hooks/useModalClose";
import {
  getBlockRackEmptyPositions,
  getBlockRackName,
  isStorageOrRetrievalEnabled,
  getBlockOrRackTotalPalletData
} from "../../../util/helper/helperFunctions";
import closeBtn from "../../../assets/icons/close.png";
import "../../../assets/css/BlockRack/blockRackModal.css";
import BlockRackTable from "./BlockRackTable";
import ExportButton from "../../Reusables/ExportButton/ExportButton";
import { Button, Flex } from "antd";
import { useSelector } from "react-redux";

const MODAL_ID = "lgv-wrapper-id";

const BlockRackModal = () => {
  const {
    setIsBlockRackModalOpen,
    isRackModal
  } = useContext(AppContext);
  const selectedBlockRackLocationData = useSelector((state)=>state.plant.selectedBlockRackLocationData)
  const [modalType, setModalType] = useState();
  const [locationData, setLocationData] = useState([]);
  const [modalLoader, setModalLoader] = useState(true);
  const [locationMetaData, setLocationMetaData] = useState({});
  const [exportData, setExportData] = useState([]);

  const [clearFiltersFn, setClearFiltersFn] = useState(null);
  const [showClearFilter, setShowClearFilter] = useState(false);

  const updateBlockRackExportData = useCallback((data) => {
    setExportData(data);
  }, []);

  const formatLocationObjectToArray = (locationObjectData) => {
    const locationList = [];
    const firstLocationObjectAvailable = Object.keys(locationObjectData)[0];
    if (isRackModal) {
      const {
        totalPallets,
        emptyPalletPositions,
        storageDisabled,
        retrievalDisabled,
        locationType,
        location
      } = locationObjectData[firstLocationObjectAvailable];
      setLocationMetaData({
        totalPallets: totalPallets,
        emptyPositions: emptyPalletPositions,
        storageDisabled: storageDisabled,
        retrievalDisabled: retrievalDisabled,
        locationType: locationType,
        location: location
      });
      Object.values(locationObjectData).forEach((location) => {
        locationList.push(location);
      });
    } else {
      const firstLocationObjectIndex = Object.keys(locationObjectData)[0];
      const firstPalletObjectIndex = Object.keys(
        locationObjectData[firstLocationObjectIndex]
      )[0];
      const firstPalletObjectValue =
        locationObjectData[firstLocationObjectIndex][firstPalletObjectIndex];

      const {
        totalPallets,
        emptyPalletPositions,
        storageDisabled,
        retrievalDisabled,
        locationType,
        location
      } = firstPalletObjectValue[0];
      setLocationMetaData({
        totalPallets: totalPallets,
        emptyPositions: emptyPalletPositions,
        storageDisabled: storageDisabled,
        retrievalDisabled: retrievalDisabled,
        locationType: locationType,
        location: location
      });
      Object.values(locationObjectData).forEach((location) => {
        Object.values(location).forEach((pallet) => {
          pallet.forEach((level) => {
            locationList.push(level);
          });
        });
      });
    }
    return locationList;
  };

  useEffect(() => {
    document.body.classList.add("prevent-scroll");
    if (Object.keys(selectedBlockRackLocationData) !== 0) {
      const formattedLocationData = formatLocationObjectToArray(
        selectedBlockRackLocationData
      );
      if (formattedLocationData.length !== 0) {
        setLocationData(formattedLocationData);
        setModalLoader(false);
      }
    }

    const modalType = isRackModal;
    if (modalType && modalType !== "") {
      setModalType(modalType);
    }
  }, []);

  const rackDetails = [
    {
      title: "Total Pallets",
      testId: "totalPalletsValue",
      value: getBlockOrRackTotalPalletData(
        selectedBlockRackLocationData,
        isRackModal
      )
    },
    {
      title: "Empty Positions",
      testId: "emptyPositionsValue",
      value: getBlockRackEmptyPositions(
        selectedBlockRackLocationData,
        isRackModal
      )
    },
    {
      title: "Storage Enabled",
      testId: "storageEnabled",
      value: isStorageOrRetrievalEnabled(
        selectedBlockRackLocationData,
        "storageDisabled",
        isRackModal
      ),
      conditionalClass:
        isStorageOrRetrievalEnabled(
          selectedBlockRackLocationData,
          "storageDisabled",
          isRackModal
        ) === "YES"
          ? "success-green-font"
          : "error-red-font"
    },
    {
      title: "Retrieval Enabled",
      testId: "retreivalEnabled",
      value: isStorageOrRetrievalEnabled(
        selectedBlockRackLocationData,
        "retrievalDisabled",
        isRackModal
      ),
      conditionalClass:
        isStorageOrRetrievalEnabled(
          selectedBlockRackLocationData,
          "retrievalDisabled",
          isRackModal
        ) === "YES"
          ? "success-green-font"
          : "error-red-font"
    },
    {
      title: "Location Type",
      testId: "locationType",
      value:
        locationMetaData.locationType !== ""
          ? locationMetaData.locationType
          : "null"
    }
  ];

  const closeModalHandler = () => {
    setIsBlockRackModalOpen(false);
    document.body.classList.remove("prevent-scroll");
  };

  useModalClose(MODAL_ID, closeModalHandler);

  const handleClearFilter = () => {
    clearFiltersFn && clearFiltersFn();
  };

  return (
    <div id={MODAL_ID} className="popup-wrapper-block-rack">
      <div
        className={`popup-container-block-rack ${
          modalType ? "overflow-scroll-block-rack" : ""
        }`}
      >
        {modalLoader ? (
          <div className="block-loader">
            <h4>Loading...</h4>
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="popup-header-block-rack">
              <div
                data-testid="modalTypeTitle"
                className="popup-title-block-rack"
              >
                {getBlockRackName(modalType, locationMetaData.location)}
              </div>
              <div className="popup-close-block-rack">
                <div
                  onClick={closeModalHandler}
                  className="close-btn"
                  id="close-btn"
                >
                  <img src={closeBtn} alt="close button" />
                </div>
              </div>
            </div>

            {/* Top Section */}
            <div className="popup-section-block-rack block-details-block-rack">
              {!modalType && (
                <h4 className="popup-section-title-block-rack">
                  Location Pallet Data
                </h4>
              )}
              {Object.values(locationMetaData).length !== 0 ? (
                <div className="details-section-block-rack">
                  {rackDetails.map(
                    ({ title, testId, value, conditionalClass = "" }) => (
                      <div key={testId} className="details-block-rack">
                        <h5 className="details-data-title-block-rack">
                          {title}
                        </h5>
                        <h5
                          data-testid={testId}
                          className={`details-data-block-rack ${conditionalClass}`}
                        >
                          {value}
                        </h5>
                      </div>
                    )
                  )}
                </div>
              ) : null}
            </div>
            <div className={modalType ? "rack-split-section-block-rack" : ""}>
              <div
                className={`left-section-wrapper-block-rack ${
                  modalType ? "left-flex-block-rack" : ""
                }`}
              >
                {modalType && (
                  <p className="section-title-block-rack">LPN Details</p>
                )}
                <Flex
                  style={{
                    width: modalType ? "98%" : "100%",
                    paddingBottom: "8px"
                  }}
                  justify="flex-end"
                  gap="small"
                >
                  {showClearFilter && (
                    <Button
                      color="danger"
                      variant="outlined"
                      style={{
                        padding: "20px",
                        borderRadius: "20px"
                      }}
                      onClick={handleClearFilter}
                    >
                      Clear Filters
                    </Button>
                  )}
                  <ExportButton
                    data={exportData}
                    type={getBlockRackName(
                      modalType,
                      locationMetaData.location
                    )}
                  />
                </Flex>
                <div
                  className={`popup-section-block-rack block-table-block-rack ${
                    modalType
                      ? "rack-table-size-block-rack"
                      : "block-table-size-block-rack"
                  }`}
                >
                  <BlockRackTable
                    locationData={locationData}
                    updateBlockRackExportData={updateBlockRackExportData}
                    setClearFiltersFn={setClearFiltersFn}
                    setShowClearFilter={setShowClearFilter}
                  />
                </div>
              </div>
              {modalType && locationData.length !== 0 ? (
                <div className="right-section-wrapper-block-rack right-flex-block-rack">
                  <p className="section-title-block-rack">Rack Front View</p>
                  <div className="rack-table-block-rack">
                    <RackFrontView locationData={locationData} />
                  </div>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BlockRackModal;
